import React, { useCallback } from 'react';
import {
  DatePicker,
  LocalizationProvider,
  DatePickerProps,
} from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment, { Moment } from 'moment';
import 'moment/locale/fr';

import i18n from '@/i18n';
import { useTranslation } from 'react-i18next';

import { CalendarDaysIcon } from '@heroicons/react/24/outline';
import JTextFieldBase from '@atoms/JTextFieldBase';
import { IconSize } from '@constants';
import { TextFieldProps, useTheme } from '@mui/material';

type IProps = DatePickerProps<Moment> & {
  handleChange: (_: string) => void;
  defaultValue?: Moment | null;
  label: React.ReactNode;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  onClear?: () => void;
  whiteBg?: boolean;
};

const JDatePicker: React.FC<IProps> = ({
  handleChange,
  defaultValue,
  label,
  disabled,
  required,
  error,
  onClear,
  whiteBg = false,
  ...props
}) => {
  moment.locale(i18n.language);

  const defaultDate = defaultValue ? moment(defaultValue) : null;
  const { t } = useTranslation();
  const theme = useTheme();
  const calendarIcon = useCallback(
    () => (
      <CalendarDaysIcon
        color={theme.palette.primary.main}
        height={IconSize.sm}
      />
    ),
    [theme],
  );
  const textFieldPlaceHolder = useCallback(
    (params: TextFieldProps) => (
      <JTextFieldBase attr={{ whiteBg: true }} {...params} />
    ),
    [],
  );
  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <DatePicker
        data-qa={`date-picker-${label}`}
        slotProps={{
          field: { clearable: true, onClear },
        }}
        label={label}
        value={defaultDate}
        onChange={(newValue) => {
          if (newValue) handleChange(newValue.format('YYYY-MM-DD'));
        }}
        slots={{
          openPickerIcon: calendarIcon,
          textField: textFieldPlaceHolder,
        }}
        disabled={disabled}
        localeText={{
          fieldDayPlaceholder: () => t('DD'),
          fieldMonthPlaceholder: () => t('MM'),
          fieldYearPlaceholder: () => t('YYYY'),
        }}
        {...props}
      />
    </LocalizationProvider>
  );
};

export default JDatePicker;
